import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card, Row, Col, Statistic, Button, Space, List, Tag, Spin, message, Tooltip, Badge
} from 'antd';
import {
  UserOutlined, ShoppingOutlined, AppstoreOutlined, EuroOutlined,
  ShopOutlined, CalendarOutlined, TeamOutlined, FileDoneOutlined,
  PlusOutlined, EyeOutlined, EditOutlined, BulbOutlined, TagsOutlined,
  DownloadOutlined, HeartOutlined, QuestionCircleOutlined, FileTextOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { useStores } from '../../stores';
import exportService from '../../services/exportService';

const AdminDashboard = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminDashboardStore, authStore } = useStores();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      await adminDashboardStore.fetchDashboardStats();
    } catch (error) {
      message.error(t('admin.dashboard.loadFailed'));
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      PENDING: 'orange',
      CONFIRMED: 'blue',
      SHIPPED: 'cyan',
      DELIVERED: 'green',
      COMPLETED: 'green',
      CANCELLED: 'red'
    };
    return statusColors[status] || 'default';
  };

  const formatActivityItem = (item) => {
    if (item.type === 'order') {
      return {
        title: t('admin.dashboard.orderActivity', { id: item.id }),
        description: (
          <>
            <div>{t('admin.dashboard.orderDescription', { customer: item.customer, partner: item.partner })}</div>
            <div>
              <Tag color={getStatusColor(item.status)}>{item.status}</Tag>
              <span style={{ marginLeft: 8, fontWeight: 'bold' }}>€{item.amount}</span>
            </div>
          </>
        ),
        timestamp: new Date(item.timestamp).toLocaleString(),
        actions: [
          <Button
            key="view"
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/partner/orders/${item.id}`)}
          >
            {t('common.view')}
          </Button>
        ]
      };
    } else if (item.type === 'appointment') {
      return {
        title: t('admin.dashboard.appointmentActivity', { id: item.id }),
        description: (
          <>
            <div>{t('admin.dashboard.appointmentDescription', { customer: item.customer, service: item.service, partner: item.partner })}</div>
            <Tag color={getStatusColor(item.status)}>{item.status}</Tag>
          </>
        ),
        timestamp: new Date(item.timestamp).toLocaleString(),
        actions: [
          <Button
            key="view"
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/partner/appointments/${item.id}`)}
          >
            {t('common.view')}
          </Button>
        ]
      };
    }
    return null;
  };

  if (adminDashboardStore.loading || !adminDashboardStore.stats) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const { stats } = adminDashboardStore;

  return (
    <div style={{ padding: '24px' }}>
      {/* Welcome Section */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: 8 }}>
          {authStore.user?.first_name ? t('admin.dashboard.welcome', { name: authStore.user.first_name }) : t('admin.dashboard.welcomeDefault')}
        </h1>
        <p style={{ fontSize: '16px', color: '#666' }}>
          {t('admin.dashboard.subtitle')}
        </p>
      </div>

      {/* Quick Actions */}
      <Card
        title={t('admin.dashboard.quickActions')}
        style={{ marginBottom: 24 }}
        bodyStyle={{ padding: '16px' }}
      >
        <Space size="middle" wrap>
          <Badge count={stats.products.awaiting_marketing} offset={[-5, 5]}>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate('/admin/products/queue')}
            >
              {t('admin.dashboard.enrichProducts', { count: stats.products.awaiting_marketing })}
            </Button>
          </Badge>
          <Button
            icon={<PlusOutlined />}
            onClick={() => navigate('/admin/users')}
          >
            {t('admin.dashboard.promoteUser')}
          </Button>
          <Badge count={stats.pending?.partner_verification || 0} offset={[-5, 5]}>
            <Button
              icon={<ShopOutlined />}
              onClick={() => navigate('/admin/partners')}
            >
              {t('admin.dashboard.managePartners')}
            </Button>
          </Badge>
          <Badge count={stats.pending?.inventory || 0} offset={[-5, 5]}>
            <Button
              icon={<AppstoreOutlined />}
              onClick={() => navigate('/admin/products')}
            >
              {t('admin.dashboard.manageProducts')}
            </Button>
          </Badge>
          <Button
            icon={<HeartOutlined />}
            onClick={() => navigate('/admin/animals')}
          >
            {t('admin.dashboard.viewAllAnimals')}
          </Button>
          <Button
            icon={<CalendarOutlined />}
            onClick={() => navigate('/admin/service-categories')}
          >
            {t('admin.dashboard.serviceCategories')}
          </Button>
          <Badge count={stats.pending?.category_suggestions || 0} offset={[-5, 5]} color="orange">
            <Button
              icon={<BulbOutlined />}
              onClick={() => navigate('/admin/category-suggestions')}
            >
              {t('admin.dashboard.categorySuggestions')}
            </Button>
          </Badge>
          <Button
            icon={<TagsOutlined />}
            onClick={() => navigate('/admin/animal-categories')}
          >
            {t('admin.dashboard.animalCategories')}
          </Button>
          <Button
            icon={<TagsOutlined />}
            onClick={() => navigate('/admin/product-categories')}
          >
            {t('admin.dashboard.productCategories')}
          </Button>
          <Badge count={stats.pending?.breed_suggestions || 0} offset={[-5, 5]} color="orange">
            <Button
              icon={<TagsOutlined />}
              onClick={() => navigate('/admin/breeds')}
            >
              {t('admin.dashboard.breeds')}
            </Button>
          </Badge>
          <Button
            icon={<HeartOutlined />}
            onClick={() => navigate('/admin/ngos')}
            style={{ color: '#eb2f96' }}
          >
            {t('admin.dashboard.ngos')}
          </Button>
          <Button
            icon={<FileTextOutlined />}
            onClick={() => navigate('/admin/invoices')}
          >
            {t('admin.dashboard.invoices')}
          </Button>
          <Badge count={stats.pending?.inquiries || 0} offset={[-5, 5]} color="red">
            <Button
              icon={<QuestionCircleOutlined />}
              onClick={() => navigate('/admin/inquiries')}
            >
              {t('admin.dashboard.inquiries')}
            </Button>
          </Badge>
          <Button
            icon={<ShoppingOutlined />}
            onClick={() => navigate('/admin/orders')}
          >
            {t('admin.dashboard.manageOrders')}
          </Button>
          <Button
            icon={<CalendarOutlined />}
            onClick={() => navigate('/admin/appointments')}
          >
            {t('admin.dashboard.manageAppointments')}
          </Button>
          <Badge count={stats.pending?.services || 0} offset={[-5, 5]}>
            <Button
              icon={<FileDoneOutlined />}
              onClick={() => navigate('/admin/services')}
            >
              {t('admin.dashboard.manageServices')}
            </Button>
          </Badge>
          <Badge count={stats.pending?.bundles || 0} offset={[-5, 5]} color="purple">
            <Button
              icon={<GiftOutlined />}
              onClick={() => navigate('/admin/bundles')}
            >
              {t('admin.dashboard.bundleRequests')}
            </Button>
          </Badge>
        </Space>
      </Card>

      {/* Export Data Section */}
      <Card
        title={<><DownloadOutlined /> {t('admin.dashboard.exportData')}</>}
        style={{ marginBottom: 24 }}
        bodyStyle={{ padding: '16px' }}
      >
        <Space size="middle" wrap>
          <Button
            icon={<UserOutlined />}
            onClick={async () => {
              const result = await exportService.exportUsers();
              if (result.success) {
                message.success(t('admin.dashboard.exportSuccess', { type: t('admin.dashboard.exportUsers') }));
              } else {
                message.error(result.error);
              }
            }}
          >
            {t('admin.dashboard.exportUsers')}
          </Button>
          <Button
            icon={<ShoppingOutlined />}
            onClick={async () => {
              const result = await exportService.exportOrders();
              if (result.success) {
                message.success(t('admin.dashboard.exportSuccess', { type: t('admin.dashboard.exportOrders') }));
              } else {
                message.error(result.error);
              }
            }}
          >
            {t('admin.dashboard.exportOrders')}
          </Button>
          <Button
            icon={<AppstoreOutlined />}
            onClick={async () => {
              const result = await exportService.exportProducts();
              if (result.success) {
                message.success(t('admin.dashboard.exportSuccess', { type: t('admin.dashboard.exportProducts') }));
              } else {
                message.error(result.error);
              }
            }}
          >
            {t('admin.dashboard.exportProducts')}
          </Button>
          <Button
            icon={<ShopOutlined />}
            onClick={async () => {
              const result = await exportService.exportPartners();
              if (result.success) {
                message.success(t('admin.dashboard.exportSuccess', { type: t('admin.dashboard.exportPartners') }));
              } else {
                message.error(result.error);
              }
            }}
          >
            {t('admin.dashboard.exportPartners')}
          </Button>
          <Button
            icon={<TeamOutlined />}
            onClick={async () => {
              const result = await exportService.exportPets();
              if (result.success) {
                message.success(t('admin.dashboard.exportSuccess', { type: t('admin.dashboard.exportAnimals') }));
              } else {
                message.error(result.error);
              }
            }}
          >
            {t('admin.dashboard.exportAnimals')}
          </Button>
          <Button
            icon={<CalendarOutlined />}
            onClick={async () => {
              const result = await exportService.exportAppointments();
              if (result.success) {
                message.success(t('admin.dashboard.exportSuccess', { type: t('admin.dashboard.exportAppointments') }));
              } else {
                message.error(result.error);
              }
            }}
          >
            {t('admin.dashboard.exportAppointments')}
          </Button>
          <Button
            icon={<FileDoneOutlined />}
            onClick={async () => {
              const result = await exportService.exportServices();
              if (result.success) {
                message.success(t('admin.dashboard.exportSuccess', { type: t('admin.dashboard.exportServices') }));
              } else {
                message.error(result.error);
              }
            }}
          >
            {t('admin.dashboard.exportServices')}
          </Button>
          <Button
            icon={<EuroOutlined />}
            onClick={async () => {
              const result = await exportService.exportInventory();
              if (result.success) {
                message.success(t('admin.dashboard.exportSuccess', { type: t('admin.dashboard.exportInventory') }));
              } else {
                message.error(result.error);
              }
            }}
          >
            {t('admin.dashboard.exportInventory')}
          </Button>
        </Space>
      </Card>

      {/* Statistics Cards - Row 1 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Tooltip title={t('admin.dashboard.clickToManageUsers')}>
            <Card
              hoverable
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/admin/users')}
            >
              <Statistic
                title={t('admin.dashboard.stats.totalUsers')}
                value={stats.users.total}
                prefix={<UserOutlined />}
                suffix={
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
                    {t('admin.dashboard.stats.customersPartners', { customers: stats.users.customers, partners: stats.users.partners })}
                  </div>
                }
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Tooltip title={t('admin.dashboard.clickToManageProducts')}>
            <Card
              hoverable
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/admin/products')}
            >
              <Statistic
                title={t('admin.dashboard.stats.totalProducts')}
                value={stats.products.total}
                prefix={<AppstoreOutlined />}
                suffix={
                  stats.products.awaiting_marketing > 0 && (
                    <Tag color="orange" style={{ marginTop: 8 }}>
                      {t('admin.dashboard.stats.awaitingMarketing', { count: stats.products.awaiting_marketing })}
                    </Tag>
                  )
                }
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Tooltip title={t('admin.dashboard.clickToManageServices')}>
            <Card
              hoverable
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/admin/services')}
            >
              <Statistic
                title={t('admin.dashboard.stats.totalServices')}
                value={stats.services.total}
                prefix={<FileDoneOutlined />}
                suffix={
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
                    {t('admin.dashboard.stats.activeServices', { count: stats.services.active })}
                  </div>
                }
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Tooltip title={t('admin.dashboard.clickToViewOrders')}>
            <Card
              hoverable
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/admin/orders')}
            >
              <Statistic
                title={t('admin.dashboard.stats.totalRevenue')}
                value={parseFloat(stats.orders.total_revenue).toFixed(2)}
                prefix={<EuroOutlined />}
                precision={2}
                suffix="€"
              />
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {/* Statistics Cards - Row 2 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Tooltip title={t('admin.dashboard.clickToViewOrders')}>
            <Card
              hoverable
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/admin/orders')}
            >
              <Statistic
                title={t('admin.dashboard.stats.totalOrders')}
                value={stats.orders.total}
                prefix={<ShoppingOutlined />}
                suffix={
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
                    <Tag color="orange">{t('admin.dashboard.stats.pendingOrders', { count: stats.orders.pending })}</Tag>
                    <Tag color="green">{t('admin.dashboard.stats.deliveredOrders', { count: stats.orders.delivered })}</Tag>
                  </div>
                }
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Tooltip title={t('admin.dashboard.clickToViewAppointments')}>
            <Card
              hoverable
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/admin/appointments')}
            >
              <Statistic
                title={t('admin.dashboard.stats.totalAppointments')}
                value={stats.appointments.total}
                prefix={<CalendarOutlined />}
                suffix={
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
                    <Tag color="orange">{t('common.pending')}</Tag>
                    <Tag color="green">{t('admin.dashboard.stats.completedAppointments', { count: stats.appointments.completed })}</Tag>
                  </div>
                }
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Tooltip title={t('admin.dashboard.clickToManagePartners')}>
            <Card
              hoverable
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/admin/partners')}
            >
              <Statistic
                title={t('admin.dashboard.stats.totalPartners')}
                value={stats.partners.total}
                prefix={<TeamOutlined />}
                suffix={
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
                    <Tag color="green">{t('admin.dashboard.stats.verifiedPartners', { count: stats.partners.verified })}</Tag>
                    <Tag color="orange">{t('admin.dashboard.stats.pendingPartners', { count: stats.partners.pending_verification })}</Tag>
                  </div>
                }
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Tooltip title={t('admin.dashboard.clickToEnrichProducts')}>
            <Card
              hoverable
              style={{
                cursor: 'pointer',
                background: stats.products.awaiting_marketing > 0 ? '#fff7e6' : '#fff'
              }}
              onClick={() => navigate('/admin/products/queue')}
            >
              <Statistic
                title={t('admin.dashboard.stats.productsAwaitingMarketing')}
                value={stats.products.awaiting_marketing}
                prefix={<FileDoneOutlined />}
                valueStyle={{
                  color: stats.products.awaiting_marketing > 0 ? '#fa8c16' : '#52c41a'
                }}
              />
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {/* Recent Activity */}
      <Card
        title={t('admin.dashboard.recentActivity')}
        extra={<span style={{ color: '#666', fontSize: '14px' }}>{t('admin.dashboard.last10Items')}</span>}
      >
        <List
          dataSource={adminDashboardStore.recentActivity}
          renderItem={(item) => {
            const formattedItem = formatActivityItem(item);
            if (!formattedItem) return null;

            return (
              <List.Item actions={formattedItem.actions}>
                <List.Item.Meta
                  title={formattedItem.title}
                  description={
                    <>
                      {formattedItem.description}
                      <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                        {formattedItem.timestamp}
                      </div>
                    </>
                  }
                />
              </List.Item>
            );
          }}
          locale={{ emptyText: t('admin.dashboard.noRecentActivity') }}
        />
      </Card>
    </div>
  );
});

export default AdminDashboard;
